import { useWebsiteConfigurationStore } from '@/stores/websiteConfigurationStore';
import { useUserStore } from '@/stores/user';
import { useModalStore } from '@/stores/modalStore';
import { useConnectivityStore } from '@/stores/connectivityStore';
import { jwtDecode } from "jwt-decode";
import { getAuth } from "firebase/auth";
import { useFirebaseApp } from 'vuefire';
import { setDoc, doc } from 'firebase/firestore';
import { useFirestore } from 'vuefire';

export default defineNuxtPlugin((nuxtApp) => {
  const websiteConfigurationStore = useWebsiteConfigurationStore();
  const modalStore = useModalStore();
  const connectivityStore = useConnectivityStore();

  let registerPromise = null;

  const helpers = {
    isOffline() {
      if (!connectivityStore.isOnline) {
        connectivityStore.notifyBlocked();
        return true;
      }
      return false;
    },
    findCheapestPackage(packages) {
      return packages.reduce((lowest, obj) => {
        if (obj.min < lowest.min) {
          return obj;
        }
        return lowest;
      });
    },
    decodeToken(token) {
      return jwtDecode(token);
    },
    openDialog(name, params, size = 'xl') {
      if (this.isOffline()) return;
      modalStore.openDialog(name, params, size);
    },
    openAuthorizedDialog(name, params, size = 'xl') {
      if (this.isOffline()) return;
      if (nuxtApp.$currentUser()) {
        this.openDialog(name, params, size);
      } else {
        this.openDialog('Auth', {intent: {name, params, size}});
      }
    },
    openAuthorizedLink(path, query = {}) {
      if (this.isOffline()) return;
      let route = {
        path,
        query
      };
      if (nuxtApp.$currentUser()) {
        navigateTo(route);
      } else {
        this.openDialog('Auth', {intent: route});
      }
    },
    closeDialog() {
      modalStore.closeDialog();
    },
    findShortestStayPackage(packages) {
      return packages.sort((a, b) => a.min - b.min)[0];
    },
    getAvailabilityColorClass(availability) {
      if (Array.isArray(availability) || !availability) {
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300';
      }
      if (!availability.available_from_date) {
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300';
      }
      if (availability.precision === 'now') {
        if (availability.available_from_date) {
          return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
        }
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300';
      }

      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';

    },
    getAvailabilityLabel(availability) {
      if (Array.isArray(availability) || !availability) {
        return nuxtApp.$i18n.t('labels.not_available');
      }

      const normalizeDate = (date) => {
        if (!date) return null;
        // Check if date is a timestamp (integer)
        if (typeof date === 'number') {
          return new Date(date * 1000); // Convert seconds to milliseconds
        }
        // Assume it's a string (MySQL format)
        return new Date(date);
      };
      const availableFromDate = normalizeDate(availability.available_from_date);
      if (!availableFromDate) {
        return nuxtApp.$i18n.t('labels.not_available');
      }

      if (availability.precision === 'now') {
        if (availableFromDate) {
          return nuxtApp.$i18n.t('labels.available_now');
        }
        return nuxtApp.$i18n.t('labels.not_available');
      }

      if (availability.is_confirmed) {
        return (
          nuxtApp.$i18n.t('labels.exit_at') + ' ' + availableFromDate.toISOString().substr(0, 10)
        );
      } else {
        return (
          nuxtApp.$i18n.t('labels.from_date') + ' ' + availableFromDate.toISOString().substr(0, 10)
        );
      }
    },
    priceFormat(price) {
      return websiteConfigurationStore.configuration['currencyCode'].toUpperCase() + ' ' + parseInt(price);
    },
    initials(label) {
      if (!label) {
        return 'XX';
      }
      // Trim the label, split by spaces, and filter out any empty strings
      const words = label.trim().split(/\s+/);

      // Map over the array of words to get the first character of each word in uppercase
      const allInitials = words.map(word => word[0].toUpperCase());

      // Check if there is less than two initials and add a default initial if needed
      if (allInitials.length < 2) {
        allInitials.push('X');  // You can choose any default character
      }

      // Join the initials and slice to ensure exactly two letters
      return allInitials.join('').slice(0, 2);
    },
    // schema.org PostalAddress from a project location (street/district/city/country).
    // Omits parts that are missing so the structured data stays valid.
    schemaAddress(location) {
      const address = { '@type': 'PostalAddress' };
      if (location.street) address.streetAddress = location.street;
      const district = this.districtLabel(location);
      if (district) address.addressRegion = district;
      if (location.city && location.city.name) address.addressLocality = location.city.name;
      if (location.country && location.country.name) address.addressCountry = location.country.name;
      return address;
    },
    // schema.org GeoCoordinates, or null when the location has no coordinates.
    schemaGeo(location) {
      if (location.latitude == null || location.longitude == null) return null;
      return { '@type': 'GeoCoordinates', latitude: location.latitude, longitude: location.longitude };
    },
    // schema.org LocationFeatureSpecification[] from amenity/facility objects ({ label }).
    schemaAmenities(features) {
      return (features || [])
        .filter((feature) => feature && feature.label)
        .map((feature) => ({ '@type': 'LocationFeatureSpecification', name: feature.label, value: true }));
    },
    // useHead <script> descriptor for a JSON-LD block. `<` is escaped so the
    // serialized JSON can never break out of the <script> tag. The shared key
    // lets page-level blocks replace each other on navigation.
    jsonLdScript(data, key = 'ld-json') {
      return {
        key,
        type: 'application/ld+json',
        innerHTML: JSON.stringify(data).replace(/</g, '\\u003c'),
      };
    },
    // Public-facing host for the active tenant: the canonical domain when set,
    // otherwise the configured domain pattern with any wildcard stripped. Never
    // the internal Cloud Run host, so it is safe for share/canonical URLs.
    canonicalHost() {
      const config = websiteConfigurationStore.configuration || {};
      const host = config.canonicalDomain
        || (config.domain ? config.domain.replace(/^\*\.?/, '') : '');
      return host && host !== '*' ? host : null;
    },
    // Builds an absolute https URL on the tenant's public host for the given path.
    canonicalUrl(path = '/') {
      const host = this.canonicalHost();
      if (!host) return null;
      return 'https://' + host + (path.startsWith('/') ? path : '/' + path);
    },
    projectHrefByProjectListing(projectListing) {
      return (
        "/" +
        projectListing.announce.project.location.city.slug +
        "/" +
        projectListing.announce.project.slug
      );
    },
    featureSrc(feature) {
      return useRuntimeConfig().public.monthlyImagesProxyUrl + "icon-sets/flat-cloud/" + feature.name + ".svg";
    },
    projectCategoryLabels(categories) {
      return categories.map(category => category.label).join(', ');
    },
    // district comes back either as an empty array (none) or an object with a label
    districtLabel(location) {
      const district = location && location.district;
      return (district && !Array.isArray(district) && district.label) ? district.label : '';
    },
    // joins address parts with ", ", dropping any that are missing so we never
    // get a leading/trailing/double comma when street or district is absent
    joinAddress(...parts) {
      return parts.filter(Boolean).join(', ');
    },
    getConfigurationKey(key, defaultValue = null) {
      return websiteConfigurationStore.configuration[key] !== undefined 
        ? websiteConfigurationStore.configuration[key] 
        : defaultValue;
    },
    hasConfigurationKey(key) {
      return websiteConfigurationStore.configuration[key] !== undefined;
    },
    findFittingPackage(packages, amount) {
      const sortedPackages = packages.sort((a, b) => a.min - b.min);
      let fittingPackage = null;

      for (const pkg of sortedPackages) {
        if (pkg.min <= amount) {
          fittingPackage = pkg;
        } else {
          break;
        }
      }
      return fittingPackage;
    }
  };
  nuxtApp.provide('helpers', helpers);
});